import React, { useState } from 'react';
import { ShieldCheck, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.protocol + '//' + window.location.hostname + ':8080');

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const MONTHS = [
  {v:'01',l:'Jan'},{v:'02',l:'Feb'},{v:'03',l:'Mar'},{v:'04',l:'Apr'},
  {v:'05',l:'May'},{v:'06',l:'Jun'},{v:'07',l:'Jul'},{v:'08',l:'Aug'},
  {v:'09',l:'Sep'},{v:'10',l:'Oct'},{v:'11',l:'Nov'},{v:'12',l:'Dec'}
];

export default function App() {
  const [hasBusiness, setHasBusiness] = useState(true);
  const [showSSN, setShowSSN] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    borrow_amount:'', loan_purpose:'',
    first_name:'', last_name:'', email:'', phone:'',
    street:'', street2:'', city:'', state:'', zip:'',
    dob_month:'', dob_day:'', dob_year:'', rent_or_own:'',
    ssn:'',
    annual_income:'', household_income:'', credit_score:'', bankruptcy:'',
    has_business: true,
    business_name:'', business_start:'', entity_type:'', industry_type:'',
    business_description:'', monthly_revenue:'', ownership_percent:''
  });

  const set = (field) => (e) => setForm({...form, [field]: e.target.value});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(API_URL + '/api/apply', {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({...form, has_business: hasBusiness})
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        alert('Error submitting application. Please try again.');
      }
    } catch {
      alert('Connection error. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center max-w-md">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <ShieldCheck className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Application Submitted!</h2>
          <p className="text-gray-500">We'll review your information and get back to you shortly.</p>
        </div>
      </div>
    );
  }

  const inputCls = "block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const selectCls = "block w-full py-2 px-3 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans text-gray-800">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

        <div className="bg-white border-b border-gray-200 px-8 py-6 text-center">
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">
            See If You Qualify In Minutes
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Secure, encrypted, and no impact to your credit score to check rates.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-8 space-y-10">

          {/* 1. Contact Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-700 border-b border-gray-100 pb-2 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>First Name *</label>
                <input type="text" required placeholder="First Name" value={form.first_name} onChange={set('first_name')} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Last Name *</label>
                <input type="text" required placeholder="Last Name" value={form.last_name} onChange={set('last_name')} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" required placeholder="Email address" value={form.email} onChange={set('email')} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input type="tel" required placeholder="(555) 000-0000" value={form.phone} onChange={set('phone')} className={inputCls} />
              </div>
            </div>
          </section>

          {/* 2. Home Address */}
          <section>
            <h2 className="text-lg font-semibold text-gray-700 border-b border-gray-100 pb-2 mb-4">Home Address</h2>
            <div className="space-y-6">
              <div>
                <label className={labelCls}>Street Address *</label>
                <input type="text" required placeholder="123 Main St" value={form.street} onChange={set('street')} className={inputCls + " mb-3"} />
                <input type="text" placeholder="Apt, Suite, Unit (Optional)" value={form.street2} onChange={set('street2')} className={inputCls} />
              </div>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>City *</label>
                  <input type="text" required placeholder="City" value={form.city} onChange={set('city')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>State *</label>
                  <select required value={form.state} onChange={set('state')} className={selectCls}>
                    <option value="">State</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Zip Code *</label>
                  <input type="text" required placeholder="Zip Code" value={form.zip} onChange={set('zip')} className={inputCls} />
                </div>
              </div>
            </div>
          </section>

          {/* 3. Personal Details */}
          <section>
            <h2 className="text-lg font-semibold text-gray-700 border-b border-gray-100 pb-2 mb-4">Personal Details</h2>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Date of Birth *</label>
                <div className="grid grid-cols-3 gap-2">
                  <select required value={form.dob_month} onChange={set('dob_month')} className={selectCls}>
                    <option value="">Month</option>
                    {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                  <input type="text" required placeholder="DD" maxLength="2" value={form.dob_day} onChange={set('dob_day')}
                    className={inputCls + " text-center"} />
                  <input type="text" required placeholder="YYYY" maxLength="4" value={form.dob_year} onChange={set('dob_year')}
                    className={inputCls + " text-center"} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Do you rent or own your home? *</label>
                <select required value={form.rent_or_own} onChange={set('rent_or_own')} className={selectCls}>
                  <option value="">Select...</option>
                  <option value="rent">Rent</option>
                  <option value="own">Own</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Social Security Number *</label>
                <div className="relative">
                  <input type={showSSN ? "text" : "password"} required placeholder="XXX-XX-XXXX" maxLength="11" value={form.ssn} onChange={set('ssn')}
                    className={inputCls + " tracking-widest pr-10"} />
                  <button type="button" onClick={() => setShowSSN(!showSSN)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                    {showSSN ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 flex items-center">
                  <ShieldCheck className="w-3 h-3 mr-1" /> Used only for secure identity verification.
                </p>
              </div>
            </div>
          </section>

          {/* 4. Business Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-700 border-b border-gray-100 pb-2 mb-4">Business Information</h2>
            <div className="mb-6">
              <label className={labelCls}>Do you already own a business?</label>
              <select value={hasBusiness ? 'yes' : 'no'}
                onChange={(e) => setHasBusiness(e.target.value === 'yes')}
                className={selectCls + " sm:w-1/2"}>
                <option value="yes">Yes, I own a business</option>
                <option value="no">No, I am starting a business</option>
              </select>
            </div>

            {hasBusiness && (
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Business Name *</label>
                  <input type="text" required placeholder="Legal Business Name" value={form.business_name} onChange={set('business_name')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Business Start Date *</label>
                  <input type="text" required placeholder="MM/YYYY" value={form.business_start} onChange={set('business_start')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Entity Type *</label>
                  <select required value={form.entity_type} onChange={set('entity_type')} className={selectCls}>
                    <option value="">Entity Type</option>
                    <option value="llc">LLC</option>
                    <option value="corp">Corporation</option>
                    <option value="sole">Sole Proprietorship</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Industry Type *</label>
                  <input type="text" required placeholder="E.g.: Retail, Construction, Trucking..." value={form.industry_type} onChange={set('industry_type')} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Describe Your Business Activity *</label>
                  <textarea required placeholder="E.g.: Trucking company, local deliveries across 3 states..." value={form.business_description} onChange={set('business_description')}
                    rows="3" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Average Monthly Revenue *</label>
                  <select required value={form.monthly_revenue} onChange={set('monthly_revenue')} className={selectCls}>
                    <option value="">Select Range...</option>
                    <option value="0-10k">$0 - $10,000</option>
                    <option value="10k-25k">$10,000 - $25,000</option>
                    <option value="25k-50k">$25,000 - $50,000</option>
                    <option value="50k+">$50,000+</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Business Ownership % *</label>
                  <div className="relative">
                    <input type="number" required placeholder="100" min="1" max="100" value={form.ownership_percent} onChange={set('ownership_percent')}
                      className="block w-full pr-8 pl-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">%</span>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* 5. Income & Credit */}
          <section>
            <h2 className="text-lg font-semibold text-gray-700 border-b border-gray-100 pb-2 mb-4">Income & Credit</h2>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Pre-Tax Annual Income *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                  <input type="number" required placeholder="Annual Personal Income" value={form.annual_income} onChange={set('annual_income')}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Annual Household Income</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                  <input type="number" placeholder="Household Income" value={form.household_income} onChange={set('household_income')}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Estimated Credit Score *</label>
                <select required value={form.credit_score} onChange={set('credit_score')} className={selectCls}>
                  <option value="">What is your estimated credit score?</option>
                  <option value="excellent">Excellent (720+)</option>
                  <option value="good">Good (680-719)</option>
                  <option value="fair">Fair (640-679)</option>
                  <option value="poor">Poor (639 or below)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Ever filed for bankruptcy? *</label>
                <select required value={form.bankruptcy} onChange={set('bankruptcy')} className={selectCls}>
                  <option value="">Select...</option>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
          </section>

          {/* 6. Funding Details */}
          <section>
            <h2 className="text-lg font-semibold text-gray-700 border-b border-gray-100 pb-2 mb-4">Funding Details</h2>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>How much would you like to borrow? *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                  <input type="number" required placeholder="50,000" value={form.borrow_amount} onChange={set('borrow_amount')}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Loan Purpose *</label>
                <input type="text" required placeholder="Describe the purpose of the loan..." value={form.loan_purpose} onChange={set('loan_purpose')}
                  className={inputCls} />
              </div>
            </div>
          </section>

          {/* 7. Consent & Submit */}
          <section className="bg-gray-50 p-4 rounded-md border border-gray-200">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input id="consent" type="checkbox" required className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded" />
              </div>
              <div className="ml-3 text-xs text-gray-500">
                <label htmlFor="consent" className="font-medium text-gray-700 cursor-pointer">I Agree to the Terms and Conditions</label>
                <p className="mt-1">
                  You understand that by clicking the I AGREE box, you are providing written instructions to obtain information from your personal credit profile or other information from Experian <strong>for pre-qualification purposes</strong>. This is a soft pull and will not impact your credit score. You also consent to receive SMS messages and emails regarding your application.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button type="submit" disabled={submitting}
                className="w-full sm:w-auto px-10 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50">
                {submitting ? 'Submitting...' : 'See My Options'}
              </button>
            </div>
          </section>

        </form>
      </div>
    </div>
  );
}
